import { useEffect, useState } from 'react';
import { EVENT_STATUSES, type AdminEventStatsRowDto } from '@icebreaker/shared';
import { cityLabel, type TranslationKey, type Translator } from '@icebreaker/shared/i18n';
import {
  Button,
  EmptyState,
  ErrorBox,
  SectionHeading,
  Skeleton,
  Table,
  tableStyles,
  Tag,
} from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  fetchEventAttendees,
  fetchEventStats,
  fetchEventsByCity,
  fetchPlatformStats,
} from '../store/admin-stats.slice';
import { ATTENDEE_PAGE_SIZE, EventAttendeesDialog } from './event-attendees-dialog';
import styles from './admin.module.css';

const EVENT_PAGE_SIZE = 20;

/** Durum filtresi — "Tümü" + şemanın kendi durumları (yeni durum otomatik gelir). */
const STATUS_FILTERS = ['all', ...EVENT_STATUSES] as const;

/**
 * GENEL GÖRÜNÜM PANOSU (kullanıcı kararı 2026-09-02 §3) — panelin KÖKÜ.
 *
 * Kullanıcının üç sorusu, üç bölüm: "kaç etkinlik var" (sayı kartları),
 * "hangi şehirde kaç etkinlik" (oran şeritleri), "etkinliğe kimler katılıyor"
 * (etkinlik tablosu → katılımcı katmanı).
 *
 * REFERANS YOKTUR (devir paketinde pano çizilmemiş, kayıtlı boşluk). Bölümler
 * panelin mevcut desenlerinden devralınır ve `overview-screen.spec.tsx` bunları
 * ENUMERE EDEREK kilitler — bir bölüm ekrandan düşerse test kırılır.
 *
 * UYDURMA SAYI YOK (md. 9): her sayı sunucudan gelir. Veri gelmeden sayı
 * yazılmaz; yükleniyorken iskelet, hata varsa hata kutusu çizilir.
 */
export function OverviewScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const stats = useAppSelector((state: RootState) => state.adminStats.platformStats);
  const cities = useAppSelector((state: RootState) => state.adminStats.cities);
  const events = useAppSelector((state: RootState) => state.adminStats.events);
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [page, setPage] = useState(1);
  /** Sunucudan başlık gelene kadar katmanda gösterilecek ad; kapı DEĞİLDİR. */
  const [attendeeFallbackTitle, setAttendeeFallbackTitle] = useState('');

  useEffect(() => {
    dispatch(fetchPlatformStats.request());
    dispatch(fetchEventsByCity.request());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchEventStats.request({ status, page, pageSize: EVENT_PAGE_SIZE }));
  }, [dispatch, status, page]);

  const data = stats.data;
  const pageCount = Math.max(1, Math.ceil(events.total / EVENT_PAGE_SIZE));

  const openAttendees = (row: AdminEventStatsRowDto): void => {
    setAttendeeFallbackTitle(row.title);
    dispatch(
      fetchEventAttendees.request({
        eventId: row.id,
        query: { page: 1, pageSize: ATTENDEE_PAGE_SIZE },
      }),
    );
  };

  return (
    <section className={styles.screen}>
      <h1 className={styles.title}>{t.t('admin.overview.title')}</h1>
      <p className={styles.lead}>{t.t('admin.overview.lead')}</p>

      {/* --- 1. Platform sayıları ------------------------------------------- */}
      <ErrorBox error={stats.error} className={styles.errorBox}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => dispatch(fetchPlatformStats.request())}
        >
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      <div className={styles.panoSection}>
        <SectionHeading title={t.t('admin.overview.statsHeading')} />
        {data === null ? (
          <Skeleton label={t.t('a11y.loadingContent')} lines={2} lineHeight={64} />
        ) : (
          <div className={styles.statsGrid}>
            <StatCard
              label={t.t('admin.overview.stats.eventsTotal')}
              value={t.formatNumber(data.eventsTotal)}
              note={t.t('admin.overview.stats.eventsTotalNote', {
                count: data.eventsPublished,
              })}
            />
            <StatCard
              label={t.t('admin.overview.stats.users')}
              value={t.formatNumber(data.usersRegistered)}
              note={t.t('admin.overview.stats.usersNote')}
            />
            <StatCard
              label={t.t('admin.overview.stats.attendances')}
              value={t.formatNumber(data.attendancesTotal)}
              note={t.t('admin.overview.stats.attendancesNote')}
            />
            <StatCard
              label={t.t('admin.overview.stats.exchanges')}
              value={t.formatNumber(data.cardExchanges)}
              note={t.t('admin.overview.stats.exchangesNote')}
            />
            <StatCard
              label={t.t('admin.overview.stats.pendingModeration')}
              value={t.formatNumber(data.pendingModeration)}
              note={t.t('admin.overview.stats.pendingModerationNote')}
            />
            <StatCard
              label={t.t('admin.overview.stats.pendingVerifications')}
              value={t.formatNumber(data.pendingVerifications)}
              note={t.t('admin.overview.stats.pendingVerificationsNote')}
            />
          </div>
        )}
      </div>

      {/* --- 2. Şehre göre etkinlik ----------------------------------------- */}
      <div className={styles.panoSection}>
        <div className={styles.panoSectionHead}>
          <h2 className={styles.panoSectionTitle}>{t.t('admin.overview.citiesTitle')}</h2>
        </div>
        <ErrorBox error={cities.error} className={styles.errorBox}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => dispatch(fetchEventsByCity.request())}
          >
            {t.t('common.retry')}
          </Button>
        </ErrorBox>
        {cities.phase === 'loading' && cities.items.length === 0 ? (
          <Skeleton label={t.t('a11y.loadingContent')} lines={4} />
        ) : cities.items.length === 0 && cities.phase === 'succeeded' ? (
          <EmptyState
            title={t.t('admin.overview.citiesEmptyTitle')}
            body={t.t('admin.overview.citiesEmptyBody')}
          />
        ) : (
          <ul className={styles.breakdown}>
            {cities.items.map((city) => (
              <li key={city.cityCode} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>
                  {cityLabel(city.cityCode, (key) => t.t(key)) ?? city.cityCode}
                </span>
                <span className={styles.breakdownValue}>
                  {t.t('admin.overview.cityEvents', { count: city.eventCount })}
                </span>
                <span className={styles.breakdownTrack} aria-hidden="true">
                  <span
                    className={styles.breakdownFill}
                    style={{ width: `${share(city.eventCount, cities.locatedTotal)}%` }}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Çevrimiçi satırı YALNIZCA gerçekten varsa çizilir — sıfır basılmaz. */}
        {cities.onlineCount > 0 ? (
          <div className={styles.breakdownPlain}>
            <span>{t.t('admin.overview.onlineRow')}</span>
            <span className={styles.breakdownValue}>
              {t.t('admin.overview.cityEvents', { count: cities.onlineCount })}
            </span>
          </div>
        ) : null}
        <p className={styles.panoNote}>{t.t('admin.overview.citiesNote')}</p>
      </div>

      {/* --- 3. Etkinlik tablosu -------------------------------------------- */}
      <div className={styles.panoSection}>
        <div className={styles.panoSectionHead}>
          <h2 className={styles.panoSectionTitle}>{t.t('admin.overview.eventsTitle')}</h2>
        </div>

        <div
          role="group"
          aria-label={t.t('admin.overview.statusFilterLabel')}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ib-space-3)' }}
        >
          {STATUS_FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              className={[styles.filterBtn, status === value ? styles.filterBtnActive : '']
                .filter(Boolean)
                .join(' ')}
              aria-pressed={status === value}
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
            >
              {t.t(`admin.overview.statuses.${value}` as TranslationKey)}
            </button>
          ))}
        </div>

        <ErrorBox error={events.error} className={styles.errorBox}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              dispatch(fetchEventStats.request({ status, page, pageSize: EVENT_PAGE_SIZE }))
            }
          >
            {t.t('common.retry')}
          </Button>
        </ErrorBox>

        <div className={styles.tableWrap}>
          {events.phase === 'loading' && events.items.length === 0 ? (
            <Skeleton label={t.t('a11y.loadingContent')} lines={5} />
          ) : events.items.length === 0 && events.phase === 'succeeded' ? (
            <EmptyState
              title={t.t('admin.overview.eventsEmptyTitle')}
              body={t.t('admin.overview.eventsEmptyBody')}
              steps={[
                { title: t.t('admin.overview.eventsEmptyStepFilter') },
                { title: t.t('admin.overview.eventsEmptyStepWait') },
              ]}
              /* Tek dolu eylem, YALNIZCA daraltma varken (README 4.7). */
              action={
                status === 'all' ? undefined : (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setStatus('all');
                      setPage(1);
                    }}
                  >
                    {t.t('admin.overview.eventsEmptyResetCta')}
                  </Button>
                )
              }
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t.t('admin.overview.table.event')}</th>
                  <th>{t.t('admin.overview.table.city')}</th>
                  <th>{t.t('admin.overview.table.date')}</th>
                  <th>{t.t('admin.overview.table.status')}</th>
                  <th>{t.t('admin.overview.table.attendees')}</th>
                  <th aria-hidden="true" className={styles.actionsCell} />
                </tr>
              </thead>
              <tbody>
                {events.items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className={styles.eventCell}>
                        <span className={styles.personName}>{row.title}</span>
                        <span className={styles.personMail}>
                          {row.organizationName ?? t.t('admin.overview.noOrganization')}
                        </span>
                      </span>
                    </td>
                    <td className={tableStyles.cellMuted}>
                      {cityLabel(row.cityCode, (key) => t.t(key)) ?? t.t('admin.overview.noCity')}
                    </td>
                    <td className={styles.cellMeta}>
                      {t.formatDate(row.startsAt, { dateStyle: 'medium' })}
                    </td>
                    <td>
                      <Tag variant={statusVariant(row.status)}>{statusLabel(t, row.status)}</Tag>
                    </td>
                    <td className={styles.numberCell}>
                      <span className={styles.personName}>{t.formatNumber(row.attendeeCount)}</span>
                      <span className={styles.personMail}>
                        {t.t('admin.overview.checkedIn', { count: row.checkedInCount })}
                      </span>
                    </td>
                    <td className={styles.actionsCell}>
                      <span className={styles.rowActions}>
                        <Button variant="ghost" size="sm" onClick={() => openAttendees(row)}>
                          {t.t('admin.overview.attendeesCta')}
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {pageCount > 1 ? (
          <div className={styles.pager}>
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              {t.t('admin.overview.prevPage')}
            </Button>
            <span className={styles.pagerStatus} role="status">
              {t.t('admin.overview.pageStatus', {
                page: t.formatNumber(page),
                pageCount: t.formatNumber(pageCount),
              })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={!events.hasNext}
              onClick={() => setPage((current) => current + 1)}
            >
              {t.t('admin.overview.nextPage')}
            </Button>
          </div>
        ) : null}
      </div>

      <EventAttendeesDialog fallbackTitle={attendeeFallbackTitle} />
    </section>
  );
}

interface StatCardProps {
  readonly label: string;
  readonly value: string;
  readonly note: string;
}

function StatCard({ label, value, note }: StatCardProps) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>{value}</div>
      <div className={styles.kpiNote}>{note}</div>
    </div>
  );
}

/** Payda 0 ise şerit çizilmez (0'a bölme yok, sahte oran yok). */
function share(value: number, total: number): number {
  return total === 0 ? 0 : Math.round((value / total) * 100);
}

function statusLabel(t: Translator, status: AdminEventStatsRowDto['status']): string {
  return t.t(`admin.overview.statuses.${status}` as TranslationKey);
}

function statusVariant(status: AdminEventStatsRowDto['status']): 'outline' | 'success' | 'danger' {
  if (status === 'published') return 'success';
  if (status === 'cancelled') return 'danger';
  return 'outline';
}
